import { useEffect, useState, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Database, Plug, RefreshCw, Search, Play, Globe } from "lucide-react";
import { SincronizacionDAT } from "@/components/dat/SincronizacionDAT";
import { LoadTrafico } from "@/components/dat/LoadTrafico";

const celda = (v) => {
  if (v == null) return <span className="text-muted-foreground">—</span>;
  if (typeof v === "object") return <span title={JSON.stringify(v)}>{JSON.stringify(v).slice(0, 120)}</span>;
  const s = String(v);
  return <span title={s}>{s.length > 120 ? s.slice(0, 120) + "…" : s}</span>;
};

export default function IntegracionDAT() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ host: "", port: 5432, dbname: "", user: "", password: "", sslmode: "require" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [ipInfo, setIpInfo] = useState(null);
  const [esquema, setEsquema] = useState(null);
  const [esquema404, setEsquema404] = useState(false);
  const [exploring, setExploring] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [expandida, setExpandida] = useState(null);
  const [sample, setSample] = useState(null);
  const [sampleTarget, setSampleTarget] = useState(null);
  const [sampleFiltro, setSampleFiltro] = useState({ columna: "", valor: "" });
  const [sampleCount, setSampleCount] = useState(null);
  const [sql, setSql] = useState("");
  const [limite, setLimite] = useState(100);
  const [running, setRunning] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState(null);

  const loadCfg = useCallback(async () => {
    try {
      const { data } = await api.get("/dat/conexion");
      setCfg(data);
      setForm((f) => ({ ...f, host: data.host || "", port: data.port || 5432, dbname: data.dbname || "", user: data.user || "", sslmode: data.sslmode || "require" }));
      if (data.ultimo_test) { setTestResult(data.ultimo_test); setTestError(data.ultimo_test.ok ? null : data.ultimo_test.mensaje); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const loadEsquema = useCallback(async () => {
    try {
      const { data } = await api.get("/dat/esquema");
      setEsquema(data);
      setEsquema404(false);
    } catch (e) {
      if (e.response?.status === 404) setEsquema404(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (user?.rol === "dueno") { loadCfg(); loadEsquema(); }
  }, [user, loadCfg, loadEsquema]);

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = { host: form.host, port: Number(form.port), dbname: form.dbname, user: form.user, sslmode: form.sslmode };
      if (form.password) payload.password = form.password;
      await api.put("/dat/conexion", payload);
      toast.success("Conexión guardada");
      setForm((f) => ({ ...f, password: "" }));
      setTestResult(null);
      setTestError(null);
      await loadCfg();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const probar = async () => {
    setTesting(true);
    setTestError(null);
    try {
      const { data } = await api.post("/dat/conexion/probar");
      setTestResult(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setTestError(msg);
      setTestResult(null);
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const verIp = async () => {
    try {
      const { data } = await api.get("/dat/conexion/ip-salida");
      setIpInfo(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const explorar = async () => {
    setExploring(true);
    try {
      const { data } = await api.post("/dat/esquema/explorar");
      setEsquema(data);
      setEsquema404(false);
      toast.success("Esquema explorado y guardado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setExploring(false);
    }
  };

  const abrirMuestra = async (t, filtro = { columna: "", valor: "" }) => {
    setSampleTarget(t);
    setSample(null);
    setSampleCount(null);
    try {
      const params = { esquema: t.esquema, limite: 20 };
      if (filtro.columna && filtro.valor) { params.columna = filtro.columna; params.valor = filtro.valor; }
      const { data } = await api.get(`/dat/tablas/${t.nombre}/muestra`, { params });
      setSample(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const contarFilas = async () => {
    try {
      const { data } = await api.get(`/dat/tablas/${sampleTarget.nombre}/conteo`, { params: { esquema: sampleTarget.esquema } });
      setSampleCount(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const ejecutarConsulta = async () => {
    setRunning(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const { data } = await api.post("/dat/consulta", { sql, limite: Number(limite) });
      setQueryResult(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setQueryError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  if (user?.rol !== "dueno") {
    return <PageHeader title="Integración DAT" subtitle="Solo el Dueño puede configurar la integración con DAT." />;
  }

  const tablasFiltradas = (esquema?.tablas || []).filter((t) =>
    !busqueda || t.nombre.toLowerCase().includes(busqueda.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Integración DAT"
        subtitle="Conexión de solo lectura a la base de datos del sistema DAT (datsystem.mx). La plataforma nunca escribe en DAT."
      />

      <Card className="mb-5">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Plug className="h-5 w-5 text-primary" /> Conexión</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {cfg?.password_error && (
            <p className="rounded border border-red-300 bg-red-50 p-2 text-sm font-medium text-red-700" data-testid="dat-password-error">
              La contraseña guardada no se puede descifrar (cambió la clave de cifrado del servidor). Volvé a cargarla y guardá.
            </p>
          )}
          <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Host</Label>
              <Input className="mt-1" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="dat-host" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Puerto</Label>
              <Input className="mt-1" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} data-testid="dat-port" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Base de datos</Label>
              <Input className="mt-1" value={form.dbname} onChange={(e) => setForm({ ...form, dbname: e.target.value })} data-testid="dat-dbname" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Usuario</Label>
              <Input className="mt-1" value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} data-testid="dat-user" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Contraseña</Label>
              <Input className="mt-1" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={cfg?.password_configurada ? `Guardada (termina en ${cfg.password_last4}). Dejar vacío para conservarla` : "Contraseña"}
                data-testid="dat-password" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">SSL</Label>
              <select className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.sslmode}
                onChange={(e) => setForm({ ...form, sslmode: e.target.value })} data-testid="dat-sslmode">
                <option value="require">require (recomendado, es lo que exige AWS RDS)</option>
                <option value="prefer">prefer</option>
                <option value="disable">disable</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={guardar} disabled={saving} data-testid="dat-save">{saving ? "Guardando..." : "Guardar cambios"}</Button>
            <Button variant="outline" onClick={probar} disabled={testing} className="gap-2" data-testid="dat-test">
              <RefreshCw className={`h-4 w-4 ${testing ? "animate-spin" : ""}`} /> {testing ? "Probando..." : "Probar conexión"}
            </Button>
            <Button variant="outline" onClick={verIp} className="gap-2" data-testid="dat-ip">
              <Globe className="h-4 w-4" /> Ver IP de salida
            </Button>
          </div>
          {testResult?.ok && (
            <div className="rounded border border-green-300 bg-green-50 p-3 text-sm" data-testid="dat-test-ok">
              <p className="font-semibold text-green-800">{testResult.mensaje} ({testResult.latencia_ms} ms)</p>
              <p className="text-xs text-green-900">Versión: {testResult.version} · Usuario: {testResult.usuario} · Base: {testResult.base}</p>
              <p className="text-xs text-green-900">Réplica de lectura: {testResult.es_replica ? "Sí" : "No"} · Sesión de solo lectura: {testResult.solo_lectura ? "Sí" : "No"}</p>
              <p className="text-xs text-green-900">
                Esquemas: {(testResult.esquemas || []).map((e) => `${e.esquema} (${e.tablas} tablas)`).join(", ") || "—"} · {testResult.tablas_visibles} tablas visibles
              </p>
            </div>
          )}
          {testError && (
            <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="dat-test-error">{testError}</p>
          )}
          {testResult && !testResult.ok && !testError && (
            <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="dat-test-last-error">
              Última prueba ({testResult.fecha?.slice(0, 19).replace("T", " ")}): {testResult.mensaje}
            </p>
          )}
          {ipInfo && (
            <div className="rounded border border-border bg-slate-50 p-3 text-sm" data-testid="dat-ip-result">
              {ipInfo.ip
                ? <p className="font-mono">IP de salida de la plataforma: {ipInfo.ip}</p>
                : <p className="text-red-700">{ipInfo.error}</p>}
              <p className="mt-1 text-xs text-muted-foreground">
                Si la prueba de conexión falla por tiempo de espera, esta es la IP que el administrador de DAT tiene que autorizar en el firewall del servidor (Security Group de AWS, puerto de PostgreSQL).
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <SincronizacionDAT />
      <LoadTrafico />{/* P46 */}

      <Card className="mb-5">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Database className="h-5 w-5 text-primary" /> Esquema de la base</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={explorar} disabled={exploring} className="gap-2" data-testid="dat-explore">
              <RefreshCw className={`h-4 w-4 ${exploring ? "animate-spin" : ""}`} /> {exploring ? "Explorando (puede tardar hasta 1 minuto)..." : "Explorar esquema"}
            </Button>
            {esquema && (
              <p className="text-sm text-muted-foreground" data-testid="dat-schema-summary">
                Última exploración: {esquema.fecha?.slice(0, 19).replace("T", " ")} — {esquema.total_tablas} tablas, {esquema.total_vistas} vistas, {esquema.total_columnas} columnas
              </p>
            )}
          </div>
          {esquema404 && <p className="text-sm text-muted-foreground">Todavía no se exploró el esquema.</p>}
          {esquema && (
            <>
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Buscar tabla por nombre" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="pl-9" data-testid="dat-schema-search" />
              </div>
              <div className="overflow-x-auto rounded-md border border-border bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Esquema</TableHead>
                      <TableHead>Tabla</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Filas</TableHead>
                      <TableHead className="text-right">Columnas</TableHead>
                      <TableHead>Campos sync</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tablasFiltradas.map((t) => (
                      <Fragment key={`${t.esquema}.${t.nombre}`}>
                        <TableRow data-testid={`dat-schema-row-${t.nombre}`}>
                          <TableCell className="text-xs">{t.esquema}</TableCell>
                          <TableCell className="font-mono text-xs font-medium">{t.nombre}</TableCell>
                          <TableCell className="text-xs">{t.tipo}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{t.tipo === "vista" ? "—" : (t.filas_estimadas ?? "—")}</TableCell>
                          <TableCell className="text-right text-xs">{t.columnas?.length ?? 0}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{t.campos_sync?.length ? t.campos_sync.join(", ") : "—"}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => setExpandida(expandida === `${t.esquema}.${t.nombre}` ? null : `${t.esquema}.${t.nombre}`)} data-testid={`dat-columns-${t.nombre}`}>Columnas</Button>
                            <Button size="sm" variant="ghost" onClick={() => abrirMuestra(t)} data-testid={`dat-sample-${t.nombre}`}>Ver datos</Button>
                          </TableCell>
                        </TableRow>
                        {expandida === `${t.esquema}.${t.nombre}` && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-slate-50">
                              <div className="grid grid-cols-1 gap-1 p-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
                                {(t.columnas || []).map((c) => (
                                  <span key={c.nombre} className="font-mono">
                                    {c.nombre} <span className="text-muted-foreground">{c.tipo}</span>
                                    {c.pk && <span className="ml-1 rounded bg-amber-100 px-1 text-amber-700">PK</span>}
                                    {c.nulo && <span className="ml-1 text-muted-foreground">nulo</span>}
                                  </span>
                                ))}
                              </div>
                              {(t.fks || []).length > 0 && (
                                <div className="border-t border-border p-2 text-xs text-muted-foreground">
                                  {(t.fks || []).map((f, i) => (
                                    <span key={i} className="mr-3 font-mono">{f.columna} → {f.tabla_destino}.{f.columna_destino}</span>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                    {tablasFiltradas.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Sin tablas con ese filtro.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="mb-5">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Play className="h-5 w-5 text-primary" /> Consulta de solo lectura (avanzado)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Textarea className="font-mono text-sm" rows={6} value={sql} onChange={(e) => setSql(e.target.value)}
            placeholder="SELECT ... (solo lectura, una sola sentencia)" data-testid="dat-sql" />
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Límite</Label>
              <Input className="mt-1 w-28" type="number" min={1} max={500} value={limite} onChange={(e) => setLimite(e.target.value)} data-testid="dat-limit" />
            </div>
            <Button onClick={ejecutarConsulta} disabled={running || !sql.trim()} className="gap-2" data-testid="dat-run">
              <Play className={`h-4 w-4 ${running ? "animate-pulse" : ""}`} /> {running ? "Ejecutando..." : "Ejecutar"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Solo se aceptan consultas SELECT o WITH, una por vez. El resultado se limita al máximo indicado.</p>
          {queryError && <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="dat-query-error">{queryError}</p>}
          {queryResult && (
            <div data-testid="dat-query-result">
              <p className="mb-2 text-sm text-muted-foreground">{queryResult.devueltas} filas en {queryResult.duracion_ms} ms</p>
              <div className="overflow-x-auto rounded-md border border-border bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>{queryResult.columnas.map((c) => <TableHead key={c} className="font-mono text-xs">{c}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {queryResult.filas.map((f, i) => (
                      <TableRow key={i}>
                        {queryResult.columnas.map((c) => <TableCell key={c} className="font-mono text-xs">{celda(f[c])}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!sampleTarget} onOpenChange={(open) => { if (!open) setSampleTarget(null); }}>
        <DialogContent className="max-w-6xl" data-testid="dat-sample-modal">
          <DialogHeader><DialogTitle className="font-heading">Muestra: {sampleTarget?.esquema}.{sampleTarget?.nombre}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Columna</Label>
              <select className="mt-1 block rounded-md border border-input bg-background px-3 py-2 text-sm" value={sampleFiltro.columna}
                onChange={(e) => setSampleFiltro({ ...sampleFiltro, columna: e.target.value })} data-testid="dat-sample-column">
                <option value="">(todas)</option>
                {(sample?.columnas || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Valor</Label>
              <Input className="mt-1" value={sampleFiltro.valor} onChange={(e) => setSampleFiltro({ ...sampleFiltro, valor: e.target.value })} data-testid="dat-sample-value" />
            </div>
            <Button size="sm" variant="outline" onClick={() => abrirMuestra(sampleTarget, sampleFiltro)} data-testid="dat-sample-filter">Filtrar</Button>
            <Button size="sm" variant="outline" onClick={contarFilas} data-testid="dat-sample-count">Contar filas</Button>
            {sampleCount != null && <span className="text-sm" data-testid="dat-sample-count-result">Total real: {sampleCount}</span>}
          </div>
          {!sample && <p className="py-6 text-center text-sm text-muted-foreground">Cargando...</p>}
          {sample && (
            <>
              <p className="text-sm text-muted-foreground">
                {sample.devueltas} filas (ordenado por {sample.ordenado_por || "—"}) en {sample.duracion_ms} ms
              </p>
              <div className="overflow-x-auto rounded-md border border-border bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>{sample.columnas.map((c) => <TableHead key={c} className="font-mono text-xs">{c}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {sample.filas.map((f, i) => (
                      <TableRow key={i}>
                        {sample.columnas.map((c) => <TableCell key={c} className="font-mono text-xs">{celda(f[c])}</TableCell>)}
                      </TableRow>
                    ))}
                    {sample.filas.length === 0 && (
                      <TableRow><TableCell colSpan={sample.columnas.length || 1} className="py-6 text-center text-muted-foreground">Sin filas.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
